import clsx from "clsx";
import { isEmpty } from "lodash";
import React from "react";
import { POPOVER_RESTRICTED } from "../../../../common/components/modals/constants";
import { Popover } from "../../../../common/components/modals/Popover";
import useRoleDescriptions from "../../../../common/roles/useRoleDescriptions";
import useRolesRestrictions from "../../../../common/roles/useRolesRestrictions";
import useRolesVisibility from "../../../../common/roles/useRolesVisibility";
import SimpleCheckbox from "../../components/checkboxes/SimpleCheckbox";
import { useCustomizeQuickSearchVisibilityState } from "./useCustomizeQuickSearchVisibilityState";
import { visibilitySelectorType } from "../../../../common/types/common";
import {
	customizeQuickSearchRadiosType,
	customizeQuickSearchSelectedChoicesType,
	requiredRolesType,
} from "../../../../common/types/search/search";

interface CustomizeQuickSearchCheckboxProps {
	checked: boolean;
	criterionKey: string;
	disabled?: boolean;
	label: string;
	onChange: (newInput: customizeQuickSearchSelectedChoicesType) => void;
	radios: customizeQuickSearchRadiosType;
	requiredRoles?: requiredRolesType;
	selectedCriteria: customizeQuickSearchSelectedChoicesType;
	visibilitySelector?: visibilitySelectorType;
}

export function CustomizeQuickSearchCheckbox({
	checked,
	criterionKey,
	label,
	disabled,
	onChange,
	radios,
	requiredRoles,
	selectedCriteria,
	visibilitySelector,
}: CustomizeQuickSearchCheckboxProps) {
	const visible = useCustomizeQuickSearchVisibilityState(visibilitySelector);
	const { hasRequiredToOpenRoles } = useRolesRestrictions(requiredRoles);
	const { hasRequiredRoles } = useRolesVisibility(requiredRoles);
	const isRestricted = !hasRequiredToOpenRoles;
	const { roleDescriptions } = useRoleDescriptions();

	if (!visible || !hasRequiredRoles) {
		return null;
	}

	const handleSelectedCriteriaInputChange = (key: string) => {
		let newInput;
		const index = selectedCriteria.indexOf(key);

		if (selectedCriteria.includes(key)) {
			newInput = selectedCriteria.delete(index);
		} else {
			newInput = selectedCriteria.push(key);
		}

		onChange(newInput);
	};

	const classes = clsx("grid__col-6", {
		"grid__col-md-12": radios.length === 1,
		"grid__col-xl-4": radios.length === 1,
		"grid__col-xl-3": radios.length === 0,
		"restricted-checkbox": isRestricted,
	});

	const getRoleDescription = () => {
		const criterionRequiredRoles = requiredRoles?.openForRoles;
		if (criterionRequiredRoles?.length > 0 && !isEmpty(roleDescriptions)) {
			return criterionRequiredRoles.map((role: string, index: number) =>
				index > 0
					? ` or ${roleDescriptions[role]}`
					: roleDescriptions[role],
			);
		}
		return "";
	};

	const restrictedIcon = isRestricted && (
		<Popover
			classes="popover-grey"
			toggleClasses="btn--icon btn--restricted"
			modalId={POPOVER_RESTRICTED}
			offsetValue={{
				mainAxis: 0,
				crossAxis: -5,
			}}
		>
			<p>
				This information requires {getRoleDescription()} subscription.
			</p>
			<a
				href="/account-management/subscription"
				className="btn btn--link"
			>
				Add to Subscription
			</a>
		</Popover>
	);

	return (
		<div className={classes}>
			<SimpleCheckbox
				checked={checked}
				disabled={isRestricted || disabled}
				itemKey={criterionKey}
				inputId={`customize_quick_search_${criterionKey}`}
				onToggle={handleSelectedCriteriaInputChange}
				value={label}
			/>
			{restrictedIcon}
		</div>
	);
}
